/**
 * LLM Client - v13 Section 6.10-6.11
 *
 * High-level client with budget management, circuit breaker, and fallback chain.
 *
 * Fallback Chain (v13 Section 6.11.3):
 * 1. Original request with primary provider
 * 2. Retry same model (up to 3 times with exponential backoff)
 * 3. Downgrade to cheaper model
 * 4. Try alternative provider (e.g., OpenAI → Anthropic)
 * 5. Check cache for similar requests
 * 6. Use local LLM (WebLLM) as last resort
 *
 * @see docs/architecture/extracts/llm-cost-6.10.md
 */
import { OPERATION_LIMITS } from './types';
import { BudgetManager, DailyBudgetExceededError } from '../budget';
import { LLMCache } from '../cache';
import { LLMProviderType } from './base';
import { LLMEventEmitter } from '../events/event-emitter';
import { getModelPricing } from './registry';
/**
 * Budget exceeded error
 */
export class BudgetExceededError extends Error {
    constructor(message) {
        super(message);
        this.name = 'BudgetExceededError';
    }
}
/**
 * Request deferred error
 */
export class RequestDeferredError extends Error {
    constructor(message) {
        super(message);
        this.name = 'RequestDeferredError';
    }
}
/**
 * All fallback options exhausted error
 */
export class AllFallbacksExhaustedError extends Error {
    constructor(message) {
        super(message);
        this.name = 'AllFallbacksExhaustedError';
    }
}
/**
 * Model downgrade mappings (quality → standard → fast)
 */
const MODEL_DOWNGRADES = {
    // OpenAI downgrades
    'gpt-4o': 'gpt-4o-mini',
    'gpt-4-turbo': 'gpt-4o-mini',
    // Anthropic downgrades
    'claude-3-5-sonnet-20241022': 'claude-3-haiku-20240307',
    'claude-3-opus-20240229': 'claude-3-5-sonnet-20241022',
    // Google downgrades
    'gemini-1.5-pro': 'gemini-2.0-flash',
    // Groq downgrades
    'llama-3.3-70b-versatile': 'llama-3.1-8b-instant',
    'mixtral-8x7b-32768': 'llama-3.1-8b-instant',
};
/**
 * Alternative provider mappings
 */
const ALTERNATIVE_PROVIDERS = {
    [LLMProviderType.OPENAI]: [LLMProviderType.ANTHROPIC, LLMProviderType.GOOGLE, LLMProviderType.GROQ],
    [LLMProviderType.ANTHROPIC]: [LLMProviderType.OPENAI, LLMProviderType.GOOGLE, LLMProviderType.GROQ],
    [LLMProviderType.GOOGLE]: [LLMProviderType.OPENAI, LLMProviderType.ANTHROPIC, LLMProviderType.GROQ],
    [LLMProviderType.GROQ]: [LLMProviderType.DEEPINFRA, LLMProviderType.OPENAI, LLMProviderType.ANTHROPIC],
    [LLMProviderType.DEEPINFRA]: [LLMProviderType.GROQ, LLMProviderType.OPENAI],
    [LLMProviderType.OLLAMA]: [LLMProviderType.WEBLLM],
    [LLMProviderType.WEBLLM]: [LLMProviderType.OLLAMA],
    [LLMProviderType.MOCK]: [],
};
/**
 * Default models per provider
 */
const DEFAULT_MODELS = {
    [LLMProviderType.OPENAI]: 'gpt-4o-mini',
    [LLMProviderType.ANTHROPIC]: 'claude-3-haiku-20240307',
    [LLMProviderType.GOOGLE]: 'gemini-2.0-flash',
    [LLMProviderType.GROQ]: 'llama-3.1-8b-instant',
    [LLMProviderType.DEEPINFRA]: 'meta-llama/Llama-3.3-70B-Instruct',
    [LLMProviderType.OLLAMA]: 'llama3.2',
    [LLMProviderType.WEBLLM]: 'Llama-3.2-3B-Instruct-q4f16_1-MLC',
    [LLMProviderType.MOCK]: 'mock',
};
/**
 * LLMClient - Unified LLM client with budget enforcement and fallback chain
 */
export class LLMClient {
    constructor(config) {
        this.providers = new Map();
        this.primaryProviderType = config.providerType ?? LLMProviderType.OPENAI;
        this.providers.set(this.primaryProviderType, config.provider);
        // Add fallback providers
        if (config.fallbackProviders) {
            for (const [type, provider] of config.fallbackProviders) {
                this.providers.set(type, provider);
            }
        }
        this.budgetManager = new BudgetManager(config.budgetConfig);
        this.cache = config.cacheStore ? new LLMCache(config.cacheStore) : null;
        this.maxRetries = config.maxRetries ?? 2;
        this.retryBaseDelay = config.retryBaseDelay ?? 1000;
        this.modelByOperation = config.modelByOperation ?? {};
        this.requireExplicitModel = config.requireExplicitModel ?? false;
        this.events = new LLMEventEmitter();
    }
    /**
     * Register an additional provider
     */
    registerProvider(type, provider) {
        this.providers.set(type, provider);
    }
    /**
     * Complete a request with budget enforcement (single provider)
     */
    async complete(userId, request) {
        const startTime = Date.now();
        // Get current budget status
        const decision = await this.budgetManager.getThrottleDecision(userId);
        // Emit throttle event if applicable
        if (decision.action !== 'ok') {
            this.events.emit('throttle', {
                action: decision.action,
                budgetPercent: decision.budgetPercent,
                userId,
            });
        }
        // Handle budget exceeded
        if (decision.action === 'block') {
            throw new BudgetExceededError('Budget exceeded');
        }
        // Handle defer for non-urgent
        if (decision.action === 'defer' && !request.urgent) {
            throw new RequestDeferredError('Request deferred');
        }
        // Select model based on budget and request
        let model = request.model;
        let throttled = false;
        if (!model) {
            model = this.modelByOperation[request.operation];
        }
        if (!model) {
            if (this.requireExplicitModel) {
                throw new Error(`No explicit LLM model configured for operation "${request.operation}"`);
            }
            const tier = request.modelTier ?? OPERATION_LIMITS[request.operation].modelTier;
            model = this.budgetManager.selectModel(tier, decision.budgetPercent);
            // Check if we downgraded
            if (decision.action === 'downgrade' && request.modelTier === 'quality') {
                throttled = true;
            }
        }
        else if (decision.action === 'downgrade' && !this.requireExplicitModel) {
            // Downgrade specified model if needed
            model = this.budgetManager.selectModel('standard', decision.budgetPercent);
            throttled = true;
        }
        // Apply operation limits
        const opLimits = OPERATION_LIMITS[request.operation];
        const maxTokens = Math.min(request.maxTokens ?? opLimits.maxOutputTokens, opLimits.maxOutputTokens);
        // Execute request
        const providerRequest = {
            messages: request.messages,
            model,
            maxTokens,
            temperature: request.temperature,
            operation: request.operation,
        };
        const provider = this.providers.get(this.primaryProviderType);
        if (!provider) {
            throw new Error(`Primary provider ${this.primaryProviderType} not found`);
        }
        const estimatedCostUsd = this.estimateRequestCostUsd(model, request.messages, maxTokens, this.primaryProviderType);
        await this.budgetManager.assertDailyBudgetAvailable(userId, estimatedCostUsd);
        const response = await provider.complete(providerRequest);
        const latencyMs = Date.now() - startTime;
        const timestamp = Date.now();
        const costUsd = this.resolveTrackedCostUsd(response.usage.costUsd, estimatedCostUsd);
        // Track usage
        await this.budgetManager.trackUsage(userId, {
            model: response.model,
            inputTokens: response.usage.inputTokens,
            outputTokens: response.usage.outputTokens,
            costUsd,
            operation: request.operation,
            timestamp,
        });
        // Emit usage event (v13 Section 10.4)
        this.events.emit('usage', {
            model: response.model,
            operation: request.operation,
            inputTokens: response.usage.inputTokens,
            outputTokens: response.usage.outputTokens,
            costUsd,
            latencyMs,
            timestamp,
            userId,
        });
        return {
            ...response,
            usage: { ...response.usage, costUsd },
            throttled,
        };
    }
    /**
     * Complete a request with full fallback chain (v13 Section 6.11.3)
     */
    async completeWithFallback(userId, request) {
        const startTime = Date.now();
        // 1. Check cache FIRST (before any provider calls)
        if (this.cache) {
            const cached = await this.cache.get(userId, this.toProviderRequest(request));
            if (cached) {
                // Emit usage event for cached response
                this.events.emit('usage', {
                    model: cached.model,
                    operation: request.operation,
                    inputTokens: cached.usage.inputTokens,
                    outputTokens: cached.usage.outputTokens,
                    costUsd: 0, // No cost for cached
                    latencyMs: Date.now() - startTime,
                    timestamp: Date.now(),
                    userId,
                    cached: true,
                });
                return { ...cached, fromCache: true };
            }
        }
        // 2. Check budget
        const decision = await this.budgetManager.getThrottleDecision(userId);
        // Emit throttle event if applicable
        if (decision.action !== 'ok') {
            this.events.emit('throttle', {
                action: decision.action,
                budgetPercent: decision.budgetPercent,
                userId,
            });
        }
        // Handle budget exceeded - try cache/local only
        if (decision.action === 'block') {
            return this.handleBudgetBlocked(userId, request, startTime);
        }
        // Handle defer for non-urgent - try cache
        if (decision.action === 'defer' && !request.urgent) {
            if (this.cache) {
                const cached = await this.cache.get(userId, this.toProviderRequest(request));
                if (cached) {
                    return { ...cached, fromCache: true };
                }
            }
            throw new RequestDeferredError('Request deferred, no cache available');
        }
        // 3. Build fallback chain
        const chain = this.buildFallbackChain(request, decision);
        // 4. Execute with retries and fallback
        let lastError = null;
        let retriesAttempted = 0;
        for (const step of chain) {
            try {
                const opLimits = OPERATION_LIMITS[request.operation];
                const maxTokens = Math.min(request.maxTokens ?? opLimits.maxOutputTokens, opLimits.maxOutputTokens);
                const estimatedCostUsd = this.estimateRequestCostUsd(step.model, request.messages, maxTokens, step.providerType);
                await this.budgetManager.assertDailyBudgetAvailable(userId, estimatedCostUsd);
                const response = await this.executeWithRetry(step, request);
                retriesAttempted += response.retriesAttempted ?? 0;
                const latencyMs = Date.now() - startTime;
                const timestamp = Date.now();
                const fallbackUsed = step.reason !== 'primary' ? step.reason : undefined;
                const costUsd = this.resolveTrackedCostUsd(response.usage.costUsd, estimatedCostUsd);
                // Track usage
                await this.budgetManager.trackUsage(userId, {
                    model: response.model,
                    inputTokens: response.usage.inputTokens,
                    outputTokens: response.usage.outputTokens,
                    costUsd,
                    operation: request.operation,
                    timestamp,
                });
                // Emit usage event (v13 Section 10.4)
                this.events.emit('usage', {
                    model: response.model,
                    operation: request.operation,
                    inputTokens: response.usage.inputTokens,
                    outputTokens: response.usage.outputTokens,
                    costUsd,
                    latencyMs,
                    timestamp,
                    userId,
                    fallbackUsed,
                });
                // 5. Cache successful response
                if (this.cache) {
                    await this.cache.set(userId, this.toProviderRequest(request), {
                        ...response,
                        usage: { ...response.usage, costUsd },
                    });
                }
                return {
                    ...response,
                    usage: { ...response.usage, costUsd },
                    fallbackUsed,
                    retriesAttempted,
                };
            }
            catch (error) {
                lastError = error instanceof Error ? error : new Error(String(error));
                if (lastError instanceof DailyBudgetExceededError) {
                    throw lastError;
                }
                // Emit error event
                this.events.emit('error', {
                    message: lastError.message,
                    operation: request.operation,
                    userId,
                });
                // Continue to next fallback
            }
        }
        // 6. All fallbacks exhausted
        throw new AllFallbacksExhaustedError(`All fallback options exhausted. Last error: ${lastError?.message ?? 'unknown'}`);
    }
    /**
     * Handle budget blocked - try local providers only
     */
    async handleBudgetBlocked(userId, request, startTime) {
        // Try WebLLM first (free)
        const webllm = this.providers.get(LLMProviderType.WEBLLM);
        if (webllm) {
            try {
                const available = await webllm.isAvailable();
                if (available) {
                    const response = await webllm.complete(this.toProviderRequest(request));
                    const latencyMs = Date.now() - startTime;
                    const timestamp = Date.now();
                    // Emit usage event for free local provider
                    this.events.emit('usage', {
                        model: response.model,
                        operation: request.operation,
                        inputTokens: response.usage.inputTokens,
                        outputTokens: response.usage.outputTokens,
                        costUsd: 0, // Free local provider
                        latencyMs,
                        timestamp,
                        userId,
                        fallbackUsed: 'webllm-budget-blocked',
                    });
                    return { ...response, fallbackUsed: 'webllm-budget-blocked' };
                }
            }
            catch {
                // Continue to next option
            }
        }
        // Try Ollama (free)
        const ollama = this.providers.get(LLMProviderType.OLLAMA);
        if (ollama) {
            try {
                const available = await ollama.isAvailable();
                if (available) {
                    const response = await ollama.complete(this.toProviderRequest(request));
                    const latencyMs = Date.now() - startTime;
                    const timestamp = Date.now();
                    // Emit usage event for free local provider
                    this.events.emit('usage', {
                        model: response.model,
                        operation: request.operation,
                        inputTokens: response.usage.inputTokens,
                        outputTokens: response.usage.outputTokens,
                        costUsd: 0, // Free local provider
                        latencyMs,
                        timestamp,
                        userId,
                        fallbackUsed: 'ollama-budget-blocked',
                    });
                    return { ...response, fallbackUsed: 'ollama-budget-blocked' };
                }
            }
            catch {
                // Continue to next option
            }
        }
        // Check cache as last resort
        if (this.cache) {
            const cached = await this.cache.get(userId, this.toProviderRequest(request));
            if (cached) {
                // Emit usage event for cached response
                this.events.emit('usage', {
                    model: cached.model,
                    operation: request.operation,
                    inputTokens: cached.usage.inputTokens,
                    outputTokens: cached.usage.outputTokens,
                    costUsd: 0, // No cost for cached
                    latencyMs: Date.now() - startTime,
                    timestamp: Date.now(),
                    userId,
                    cached: true,
                });
                return { ...cached, fromCache: true };
            }
        }
        throw new BudgetExceededError('Budget exceeded and no free providers available');
    }
    /**
     * Build the fallback chain based on budget and request
     */
    buildFallbackChain(request, decision) {
        const chain = [];
        const primaryProvider = this.providers.get(this.primaryProviderType);
        if (!primaryProvider) {
            throw new Error(`Primary provider ${this.primaryProviderType} not configured`);
        }
        // Select model based on explicit request/operation first. When explicit
        // routing is required, never fall back to tier defaults or alternate
        // provider defaults: those can silently violate user settings.
        let model = request.model ?? this.modelByOperation[request.operation];
        if (!model) {
            if (this.requireExplicitModel) {
                throw new Error(`No explicit LLM model configured for operation "${request.operation}"`);
            }
            const tier = request.modelTier ?? OPERATION_LIMITS[request.operation].modelTier;
            model = this.budgetManager.selectModel(tier, decision.budgetPercent);
        }
        else if (decision.action === 'downgrade' && !this.requireExplicitModel) {
            model = this.budgetManager.selectModel('standard', decision.budgetPercent);
        }
        // Step 1: Primary request
        chain.push({
            provider: primaryProvider,
            providerType: this.primaryProviderType,
            model,
            reason: 'primary',
        });
        if (this.requireExplicitModel) {
            return chain;
        }
        // Step 2: Downgrade model (if applicable)
        const downgradedModel = model ? MODEL_DOWNGRADES[model] : undefined;
        if (downgradedModel) {
            chain.push({
                provider: primaryProvider,
                providerType: this.primaryProviderType,
                model: downgradedModel,
                reason: 'model-downgrade',
            });
        }
        // Step 3: Alternative providers
        const alternatives = ALTERNATIVE_PROVIDERS[this.primaryProviderType] ?? [];
        for (const altType of alternatives) {
            const altProvider = this.providers.get(altType);
            if (altProvider) {
                const altModel = DEFAULT_MODELS[altType];
                chain.push({
                    provider: altProvider,
                    providerType: altType,
                    model: altModel,
                    reason: `alternative-${altType}`,
                });
            }
        }
        // Step 4: Local providers (free)
        if (!this.providers.has(LLMProviderType.WEBLLM)) {
            // WebLLM not in chain yet
        }
        const webllm = this.providers.get(LLMProviderType.WEBLLM);
        if (webllm && !chain.some((s) => s.providerType === LLMProviderType.WEBLLM)) {
            chain.push({
                provider: webllm,
                providerType: LLMProviderType.WEBLLM,
                model: DEFAULT_MODELS[LLMProviderType.WEBLLM],
                reason: 'local-webllm',
            });
        }
        const ollama = this.providers.get(LLMProviderType.OLLAMA);
        if (ollama && !chain.some((s) => s.providerType === LLMProviderType.OLLAMA)) {
            chain.push({
                provider: ollama,
                providerType: LLMProviderType.OLLAMA,
                model: DEFAULT_MODELS[LLMProviderType.OLLAMA],
                reason: 'local-ollama',
            });
        }
        return chain;
    }
    /**
     * Execute a fallback step with retries
     */
    async executeWithRetry(step, request) {
        const opLimits = OPERATION_LIMITS[request.operation];
        const maxTokens = Math.min(request.maxTokens ?? opLimits.maxOutputTokens, opLimits.maxOutputTokens);
        const providerRequest = {
            messages: request.messages,
            model: step.model,
            maxTokens,
            temperature: request.temperature,
            operation: request.operation,
        };
        let lastError = null;
        let retries = 0;
        for (let attempt = 0; attempt <= this.maxRetries; attempt++) {
            try {
                // Check provider availability first
                const available = await step.provider.isAvailable();
                if (!available) {
                    throw new Error(`Provider ${step.providerType} is not available`);
                }
                const response = await step.provider.complete(providerRequest);
                // Check for error in response
                if (response.error) {
                    throw new Error(response.error);
                }
                return { ...response, retriesAttempted: retries };
            }
            catch (error) {
                lastError = error instanceof Error ? error : new Error(String(error));
                retries++;
                // Exponential backoff
                if (attempt < this.maxRetries) {
                    const delay = this.retryBaseDelay * Math.pow(2, attempt);
                    await this.sleep(delay);
                }
            }
        }
        throw lastError ?? new Error('Unknown error during retry');
    }
    /**
     * Convert ClientRequest to LLMRequest
     */
    toProviderRequest(request) {
        const opLimits = OPERATION_LIMITS[request.operation];
        return {
            messages: request.messages,
            model: request.model,
            maxTokens: Math.min(request.maxTokens ?? opLimits.maxOutputTokens, opLimits.maxOutputTokens),
            temperature: request.temperature,
            operation: request.operation,
        };
    }
    estimateRequestCostUsd(model, messages, maxOutputTokens, providerType) {
        if (providerType === LLMProviderType.WEBLLM ||
            providerType === LLMProviderType.OLLAMA ||
            providerType === LLMProviderType.MOCK ||
            model === 'local') {
            return 0;
        }
        const pricing = model ? getModelPricing(model) : null;
        if (!pricing) {
            return this.budgetManager.getUnknownRequestReserveUsd();
        }
        const inputTokens = this.estimateInputTokens(messages);
        return (inputTokens * pricing.inputPer1k + maxOutputTokens * pricing.outputPer1k) / 1000;
    }
    resolveTrackedCostUsd(providerCostUsd, estimatedCostUsd) {
        if (Number.isFinite(providerCostUsd) && providerCostUsd > 0) {
            return providerCostUsd;
        }
        return estimatedCostUsd;
    }
    estimateInputTokens(messages) {
        const chars = messages.reduce((sum, message) => sum + message.content.length, 0);
        return Math.ceil(chars / 4);
    }
    /**
     * Sleep utility
     */
    sleep(ms) {
        return new Promise((resolve) => setTimeout(resolve, ms));
    }
    /**
     * Get usage summary for a user
     */
    async getUsage(userId) {
        return this.budgetManager.getCurrentUsage(userId);
    }
    /**
     * Set usage for testing
     */
    async setUsageForTesting(userId, costUsd) {
        await this.budgetManager.setUsageForTesting(userId, costUsd);
    }
    async setDailyUsageForTesting(userId, costUsd) {
        await this.budgetManager.setDailyUsageForTesting(userId, costUsd);
    }
    /**
     * Reset monthly usage
     */
    async resetUsage(userId) {
        await this.budgetManager.resetMonthlyUsage(userId);
    }
    /**
     * Get the cache instance (for direct cache operations)
     */
    getCache() {
        return this.cache;
    }
    /**
     * Get registered providers
     */
    getProviders() {
        return new Map(this.providers);
    }
}
